import { useEffect, useState } from 'react';
import { IonIcon } from '@ionic/react';
import { qrCodeOutline, shareSocialOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { useCreateConnection } from '../../hooks/useConnections';
import { notify } from '../../services/notify';
import { Modal } from '../ui/Modal';
import { ShareQRModal } from '../ui/ShareQRModal';

interface ConnectFriendsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * "Connect with Friends" — the create flow, with three paths:
 *
 *  1. By share link: mint a one-sided connection (no recipient) and hand the
 *     `/c={id}` URL straight to the OS share sheet (`navigator.share`), with a
 *     copy-to-clipboard fallback — the same one-tap pattern as the listing
 *     share button. Whoever opens it first and signs in becomes the recipient
 *     (resolved server-side). Same row shape as the email shell-recipient flow,
 *     just with nobody pre-targeted.
 *  2. By QR: open the reusable {@link ShareQRModal} (the personal app-share QR).
 *     An already-registered user who scans it gets a scan-to-connect request via
 *     the `/share` resolve flow — so the app-share QR doubles as a connect code.
 *  3. By email: enter an address and send. If they're on HomePlated they get
 *     a request to accept; if not, they get an email invite. Idempotent
 *     server-side, so re-sending an existing connection is harmless.
 *
 * Client-side validation is inline; server-side errors surface through the
 * global error modal (apiService), same as the rest of the app, so we don't
 * duplicate them here.
 */
export const ConnectFriendsModal: React.FC<ConnectFriendsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation('connections');
  // Two independent mutation instances so each path has its own isPending.
  const createByEmail = useCreateConnection();
  const createLink = useCreateConnection();

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  // The email we actually sent — held for the success message so editing the
  // field afterward (if the user reopens) doesn't change the confirmation.
  const [sentTo, setSentTo] = useState('');
  // True when the submitted email was already an established connection. The
  // server dedupes (returns the existing accepted row, sends no new invite), so
  // we must not claim "request sent" — show an "already connected" notice.
  const [alreadyConnected, setAlreadyConnected] = useState(false);
  // QR popup. "Show your QR code" opens the reusable app-share QR (ShareQRModal),
  // which — for an already-registered scanner — now sends a scan-to-connect
  // request via the /share resolve flow. Stacks above this modal (higher z).
  const [qrOpen, setQrOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setError('');
      setSentTo('');
      setAlreadyConnected(false);
    }
  }, [isOpen]);

  const trimmed = email.trim();
  const isInvalid = !trimmed || !trimmed.includes('@');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (createByEmail.isPending) return;
    if (isInvalid) {
      setError(t('modal.errorInvalid'));
      return;
    }
    setError('');
    try {
      const connection = await createByEmail.mutateAsync({ email: trimmed });
      // An accepted row coming back means the dedupe hit an existing live
      // connection — nothing was sent, so confirm that rather than "sent".
      setAlreadyConnected(Boolean(connection.acceptedAt));
      setSentTo(trimmed);
    } catch {
      // Server-side errors (self-connect, etc.) are shown by the global error
      // modal. Keep this modal open so the user can correct and retry.
    }
  };

  const handleShareLink = async () => {
    if (createLink.isPending) return;
    setError('');
    try {
      // Empty body → backend mints a one-sided (no recipient) share-link row.
      const connection = await createLink.mutateAsync({});
      const shareUrl = `${window.location.origin}/c=${connection.id}`;

      // Hand the link straight to the OS — share sheet where available, else
      // clipboard with a confirmation toast. Mirrors the listing share button.
      if (navigator.share) {
        await navigator.share({ title: t('modal.linkShareTitle'), url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        notify.success(t('modal.copied'));
      }
      onClose();
    } catch {
      // Mint failure surfaces via the global error modal; a dismissed share
      // sheet or unavailable clipboard is a no-op. Either way, keep the modal
      // open so the user can retry.
    }
  };

  let content: React.ReactNode;
  if (sentTo) {
    // Email-sent confirmation — or an "already connected" notice when the
    // server deduped against an existing live connection.
    content = (
      <>
        <h2 className="text-lg font-bold text-foreground mb-2">
          {t(alreadyConnected ? 'modal.alreadyTitle' : 'modal.successTitle')}
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          {t(alreadyConnected ? 'modal.alreadyBody' : 'modal.successBody', { email: sentTo })}
        </p>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-lg hover:opacity-90 transition-colors"
          >
            {t('common:actions.done')}
          </button>
        </div>
      </>
    );
  } else {
    // Default: two share options (link + QR) up top, email invite below the
    // divider.
    content = (
      <>
        <h2 className="text-lg font-bold text-foreground mb-4">
          {t('modal.title')}
        </h2>

        {/* Two ways to let someone connect to you. Share-link is the filled
            primary; QR is the outline sibling. Both produce a request the other
            party accepts. */}
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={handleShareLink}
            disabled={createLink.isPending}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 active:opacity-80 disabled:opacity-50 transition-colors"
          >
            <IonIcon icon={shareSocialOutline} className="text-base" />
            {createLink.isPending ? t('modal.linkCreating') : t('modal.linkCta')}
          </button>

          {/* btn-outline (not a bare `border` utility): Ionic's button reset
              forces border-style:none, which swallows a Tailwind border and
              leaves it invisible. btn-outline + border-brand-* is the app's
              visible-outline convention. */}
          <button
            type="button"
            onClick={() => setQrOpen(true)}
            className="flex w-full items-center justify-center gap-2 rounded-lg btn-outline border-brand-primary px-4 py-2.5 text-sm font-semibold text-brand-primary active:opacity-80 transition-colors"
          >
            <IonIcon icon={qrCodeOutline} className="text-base" />
            {t('modal.qrCta')}
          </button>
        </div>

        {/* Divider: "or" */}
        <div className="flex items-center gap-3 my-4">
          <span className="h-px flex-1 bg-border" />
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            {t('modal.orDivider')}
          </span>
          <span className="h-px flex-1 bg-border" />
        </div>

        {/* Email invite */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">{t('modal.emailIntro')}</p>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('modal.placeholder')}
            required
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          />
          {error && (
            <p className="text-brand-danger text-sm text-center">{error}</p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={createByEmail.isPending}
              className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-lg hover:bg-muted disabled:opacity-50 transition-colors"
            >
              {t('common:actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={createByEmail.isPending || isInvalid}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-lg hover:opacity-90 disabled:opacity-50 transition-colors"
            >
              {createByEmail.isPending
                ? t('modal.sending')
                : t('common:actions.send')}
            </button>
          </div>
        </form>
      </>
    );
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="sm" zIndexClass="z-50">
        {content}
      </Modal>

      {/* Personal app-share QR — stacks above this modal. Scanning it sends a
          connect request (see the /share resolve flow), so it doubles as a
          "connect with me" code here. */}
      <ShareQRModal
        isOpen={qrOpen}
        onClose={() => setQrOpen(false)}
        route="/"
        zIndexClass="z-[70]"
      />
    </>
  );
};
